// src/pages/intentions/AdminFillIntention.tsx
//
// Phase 5 (v1.3.0): Admin "fill on behalf" entry point.
// Lets admin / assistant_principal users submit a teaching intention form on
// behalf of another staff member — e.g. a new teacher who hasn't started yet
// and doesn't have computer access.
//
// The form reused is the same sub-sections used by /intentions/submit. When the
// admin submits, `entered_by_staff_id` is set to the admin's own staff_code so
// there's an audit trail of who filled it out.
//
// v2.9.0: this page deliberately does NOT require a currently-open submission
// period. Catching people who missed the window is the whole point of it, so the
// admin instead picks which period the submission belongs to. Draft periods are
// excluded (not a real cycle yet); open / closed / archived are all selectable,
// with the currently-open period pre-selected when there is one.

import React, { useEffect, useMemo, useState } from 'react';
import { AlertCircle, CheckCircle, Save, Send, ShieldAlert, UserCog } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { useData } from '../../contexts/DataContext';
import { intentionPeriodsService } from '../../services/intentionPeriodsService';
import { intentionsService } from '../../services/intentionsService';
import {
  IntentionPeriod,
  AvailabilityData,
  PreferencesData,
  EmploymentDetailsData
} from '../../types/intentionPeriods';
import { TeachingIntention } from '../../types/intentions';
import AvailabilitySection from '../../components/intentions/AvailabilitySection';
import PreferencesSection from '../../components/intentions/PreferencesSection';
import RUAHPreferencesSection from '../../components/intentions/RUAHPreferencesSection';
import EmploymentDetailsSection from '../../components/intentions/EmploymentDetailsSection';
import TILActivitiesSection from '../../components/intentions/TILActivitiesSection';

const ALLOWED_ROLES = new Set(['admin', 'assistant_principal']);

// Human label for the period picker. A period flagged 'open' whose window has
// already elapsed reads as "Closed (window passed)" rather than "Open", so the
// admin isn't misled about whether staff can still submit for themselves.
const periodStatusLabel = (period: IntentionPeriod): string => {
  if (period.status !== 'open') {
    return period.status === 'closed' ? 'Closed' : 'Archived';
  }
  return intentionPeriodsService.isPeriodOpen(period) ? 'Open now' : 'Closed (window passed)';
};

const initialAvailability = (): AvailabilityData => ({
  fte: 1.0,
  ftePreference: undefined,
  dayOffPreference: undefined,
  leavePlans: ''
});

const initialPreferences = (): PreferencesData => ({
  curriculumAreas: [],
  subjects: [],
  yearLevels: [],
  ruahSubjects: []
});

const initialEmploymentDetails = (): EmploymentDetailsData => ({
  employmentType: 'ongoing',
  intentionToReturn: null,
  wishToBeConsidered: null,
  notReturningReason: '',
  hasLeavePlans: false,
  leaveType: null,
  leaveStartDate: undefined,
  leaveEndDate: undefined,
  year9StretchInterest: null,
  tilActivities: []
});

const AdminFillIntention: React.FC = () => {
  const { user } = useAuth();
  const { staff } = useData();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [periods, setPeriods] = useState<IntentionPeriod[]>([]);
  const [selectedPeriodId, setSelectedPeriodId] = useState<string>('');
  const [selectedStaffCode, setSelectedStaffCode] = useState<string>('');
  const [existingIntention, setExistingIntention] = useState<TeachingIntention | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  const [availability, setAvailability] = useState<AvailabilityData>(initialAvailability);
  const [preferences, setPreferences] = useState<PreferencesData>(initialPreferences);
  const [employmentDetails, setEmploymentDetails] = useState<EmploymentDetailsData>(initialEmploymentDetails);
  const [comments, setComments] = useState('');

  // Gate — admin or assistant_principal only
  const isAllowed = user?.role && ALLOWED_ROLES.has(user.role);

  // Load every selectable period on mount and default to the open one if there is
  // one. "No open period" is not an error state on this page.
  useEffect(() => {
    (async () => {
      try {
        setLoading(true);
        const [allPeriods, openPeriod] = await Promise.all([
          intentionPeriodsService.getAllPeriods(),
          intentionPeriodsService.getCurrentOpenPeriod()
        ]);
        // Draft periods aren't a real submission cycle yet — nothing to backfill into.
        const selectable = allPeriods.filter(p => p.status !== 'draft');
        setPeriods(selectable);
        setSelectedPeriodId(openPeriod?.id || selectable[0]?.id || '');
      } catch (e) {
        console.error('Failed to load periods', e);
        setError('Failed to load submission periods');
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const currentPeriod = useMemo(
    () => periods.find(p => p.id === selectedPeriodId) || null,
    [periods, selectedPeriodId]
  );

  const isSelectedPeriodOpen = currentPeriod
    ? intentionPeriodsService.isPeriodOpen(currentPeriod)
    : false;

  // When admin selects a staff member, pre-fill from any existing intention for that teacher
  useEffect(() => {
    if (!selectedStaffCode || !currentPeriod) {
      setExistingIntention(null);
      setAvailability(initialAvailability());
      setPreferences(initialPreferences());
      setEmploymentDetails(initialEmploymentDetails());
      setComments('');
      return;
    }
    (async () => {
      try {
        const existing = await intentionsService.getStaffIntentionForPeriod(selectedStaffCode, currentPeriod.id);
        if (existing) {
          setExistingIntention(existing);
          if (existing.availability) setAvailability(existing.availability);
          if (existing.preferences) setPreferences(existing.preferences);
          if (existing.employmentDetails) setEmploymentDetails(existing.employmentDetails);
          if (existing.additionalComments) setComments(existing.additionalComments);
        } else {
          setExistingIntention(null);
          setAvailability(initialAvailability());
          setPreferences(initialPreferences());
          setEmploymentDetails(initialEmploymentDetails());
          setComments('');
        }
      } catch (e) {
        console.error('Failed to load existing intention for staff', e);
      }
    })();
  }, [selectedStaffCode, currentPeriod]);

  const selectedStaff = useMemo(
    () => staff.find((s: any) => (s.initial || s.staffCode) === selectedStaffCode) || null,
    [staff, selectedStaffCode]
  );

  // Build the TeachingIntention payload from current form state for a given status
  const buildIntention = (status: 'draft' | 'submitted'): TeachingIntention | null => {
    if (!selectedStaff || !currentPeriod || !user?.staffCode) return null;

    const derivedIntentionToReturn =
      employmentDetails.employmentType === 'ongoing'
        ? employmentDetails.intentionToReturn || 'uncertain'
        : employmentDetails.wishToBeConsidered || 'uncertain';

    const staffCode = selectedStaff.initial || selectedStaff.staffCode || selectedStaffCode;
    const staffName = selectedStaff.name || selectedStaff.fullName || staffCode;

    return {
      id: existingIntention?.id || crypto.randomUUID(),
      staffId: staffCode,
      staffName,
      email: selectedStaff.email,
      department: selectedStaff.department || '',
      year: currentPeriod.academicYear,
      periodId: currentPeriod.id,
      status,
      availability,
      preferences,
      employmentDetails,
      additionalComments: comments,
      submittedAt: new Date(),
      lastUpdatedAt: new Date(),
      currentEmploymentType: employmentDetails.employmentType === 'ongoing' ? 'full-time' : 'fixed-term',
      currentFTE: availability.fte,
      intentionToReturn: derivedIntentionToReturn,
      desiredEmploymentStatus: derivedIntentionToReturn === 'yes' ? 'full-time' : 'not-returning',
      targetFTE: availability.fte,
      teachingPreferences: [],
      comfortableTeachingAreas: preferences.curriculumAreas.map(a => a.areaName),
      // Audit — admin who filled this in
      enteredByStaffId: user.staffCode
    };
  };

  const handleSave = async (targetStatus: 'draft' | 'submitted') => {
    if (!selectedStaffCode) {
      setError('Please pick a staff member first.');
      return;
    }
    if (!currentPeriod) {
      setError('Please pick a submission period first.');
      return;
    }
    // Say WHICH precondition failed. The old catch-all "Could not build
    // submission" sent an admin hunting through the form when the actual problem
    // was her own staff profile having no staff code (#204).
    if (!user?.staffCode) {
      setError(
        'Your own staff profile has no staff code, so this submission cannot be ' +
        'attributed to you for audit. Ask an administrator to set a staff code on ' +
        'your profile, then sign out and back in.'
      );
      return;
    }
    if (!selectedStaff) {
      setError('Could not find that staff member in the current roster. Try reloading the page.');
      return;
    }
    const payload = buildIntention(targetStatus);
    if (!payload) {
      setError('Could not build submission.');
      return;
    }

    try {
      setSaving(true);
      setError(null);
      await intentionsService.saveIntentionForPeriod(payload, currentPeriod.id);
      setSuccessMessage(
        targetStatus === 'submitted'
          ? `Submitted on behalf of ${payload.staffName} for ${currentPeriod.name}`
          : `Draft saved for ${payload.staffName} against ${currentPeriod.name}`
      );
      setExistingIntention(payload);
      setTimeout(() => setSuccessMessage(null), 4000);
    } catch (e: any) {
      console.error('Admin save failed', e);
      setError(e?.message || 'Failed to save submission on behalf of staff member.');
    } finally {
      setSaving(false);
    }
  };

  if (!isAllowed) {
    return (
      <div className="p-6 max-w-3xl mx-auto">
        <div className="bg-white rounded-lg border border-danger-border p-8 text-center">
          <ShieldAlert className="w-12 h-12 text-danger-fg mx-auto mb-3" />
          <h2 className="text-card-title text-ink-900 mb-1">Restricted</h2>
          <p className="text-sm text-ink-500">
            This page is only available to <strong>admin</strong> and <strong>assistant principal</strong> users.
          </p>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="p-6">
        <div className="animate-pulse space-y-4">
          <div className="h-8 bg-ink-200 rounded w-1/3" />
          <div className="h-64 bg-ink-200 rounded" />
        </div>
      </div>
    );
  }

  // Only a hard stop when there is no period at all to attach a submission to.
  // A closed or archived period is fine — backfilling one is the point of this page.
  if (periods.length === 0 || !currentPeriod) {
    return (
      <div className="p-6 max-w-3xl mx-auto">
        <div className="bg-white rounded-lg border border-ink-200 p-8 text-center">
          <AlertCircle className="w-10 h-10 text-ink-400 mx-auto mb-3" />
          <h2 className="text-card-title text-ink-900 mb-1">No Submission Periods</h2>
          <p className="text-sm text-ink-500">
            No submission period exists yet (draft periods can't be filled against). Create and
            open one in{' '}
            <code className="px-1 py-0.5 bg-ink-100 rounded">/admin/intention-periods</code>{' '}
            before filling intentions on behalf of staff.
          </p>
        </div>
      </div>
    );
  }

  const currentIntention =
    employmentDetails.employmentType === 'ongoing'
      ? employmentDetails.intentionToReturn
      : employmentDetails.wishToBeConsidered;
  const showDetailedSections = currentIntention === 'yes' || currentIntention === 'uncertain';

  // Sorted staff list for the picker
  const sortedStaff = [...staff].sort((a: any, b: any) =>
    (a.name || '').localeCompare(b.name || '')
  );

  return (
    <div className="p-6 max-w-4xl mx-auto">
      {/* Header */}
      <div className="bg-white rounded-lg border border-ink-200 p-6 mb-6">
        <h1 className="text-2xl font-bold text-ink-900 flex items-center gap-2">
          <UserCog className="w-6 h-6 text-navy-600" />
          Fill Intention On Behalf
        </h1>
        <p className="text-ink-500 mt-1 text-sm">
          Submit a teaching-intentions form for a staff member who can't submit themselves — a new
          teacher who has not yet started, or someone who missed the submission window. The period
          does not have to be open. Your staff code will be recorded on the submission for audit.
        </p>
        <div className="mt-3 text-xs text-ink-500">
          Entered by: <span className="font-mono tabular-nums text-ink-700">{user?.staffCode}</span>
        </div>
      </div>

      {/* Period Picker */}
      <div className="bg-white rounded-lg border border-ink-200 p-6 mb-6">
        <label htmlFor="period-picker" className="block text-sm font-medium text-ink-700 mb-2">
          Submission Period <span className="text-danger-fg">*</span>
        </label>
        <select
          id="period-picker"
          value={selectedPeriodId}
          onChange={e => setSelectedPeriodId(e.target.value)}
          disabled={saving}
          className="w-full px-3 py-2 border border-ink-300 rounded-md shadow-sm focus:ring-navy-500 focus:border-navy-500 text-sm disabled:opacity-50"
        >
          {periods.map(p => (
            <option key={p.id} value={p.id}>
              {p.name} ({p.academicYear}) — {periodStatusLabel(p)}
            </option>
          ))}
        </select>
        {!isSelectedPeriodOpen && (
          <p className="mt-2 text-xs text-warn-fg bg-warn-bg border border-warn-border rounded px-2 py-1.5">
            This period is not open for staff submissions. You can still record one here on their
            behalf. If the period's due date has already passed, a first-time submission will be
            marked <strong>late</strong>.
          </p>
        )}
      </div>

      {/* Staff Picker */}
      <div className="bg-white rounded-lg border border-ink-200 p-6 mb-6">
        <label htmlFor="staff-picker" className="block text-sm font-medium text-ink-700 mb-2">
          Staff Member <span className="text-danger-fg">*</span>
        </label>
        <select
          id="staff-picker"
          value={selectedStaffCode}
          onChange={e => setSelectedStaffCode(e.target.value)}
          className="w-full px-3 py-2 border border-ink-300 rounded-md shadow-sm focus:ring-navy-500 focus:border-navy-500 text-sm"
        >
          <option value="">— Select staff member —</option>
          {sortedStaff.map((s: any) => {
            const code = s.initial || s.staffCode;
            return (
              <option key={s.id || code} value={code}>
                {s.name || s.fullName} ({code})
              </option>
            );
          })}
        </select>
        {selectedStaff && existingIntention && (
          <p className="mt-2 text-xs text-warn-fg bg-warn-bg border border-warn-border rounded px-2 py-1.5">
            An existing {existingIntention.status} submission was found — any edits will overwrite it.
          </p>
        )}
      </div>

      {successMessage && (
        <div className="mb-6 p-4 bg-ok-bg border border-ok-border rounded-lg text-ok-fg flex items-center gap-2">
          <CheckCircle className="w-5 h-5 flex-shrink-0" />
          {successMessage}
        </div>
      )}

      {/* Form sections — only render once a staff member is picked */}
      {selectedStaffCode && (
        <div className="space-y-6">
          <EmploymentDetailsSection
            employmentDetails={employmentDetails}
            onChange={setEmploymentDetails}
            disabled={saving}
          />

          {showDetailedSections && (
            <>
              <TILActivitiesSection
                selectedActivities={employmentDetails.tilActivities}
                onChange={(activities) =>
                  setEmploymentDetails({ ...employmentDetails, tilActivities: activities })
                }
                disabled={saving}
              />

              <AvailabilitySection
                availability={availability}
                onChange={setAvailability}
                disabled={saving}
              />

              <PreferencesSection
                preferences={preferences}
                onChange={setPreferences}
                academicYear={currentPeriod.academicYear}
                disabled={saving}
              />

              <RUAHPreferencesSection
                preferences={preferences}
                onChange={setPreferences}
                academicYear={currentPeriod.academicYear}
                disabled={saving}
              />
            </>
          )}

          {/* Comments */}
          <div className="bg-white rounded-lg border border-ink-200 p-6">
            <h3 className="text-card-title text-ink-900 mb-4">Additional Comments</h3>
            <textarea
              value={comments}
              onChange={(e) => setComments(e.target.value)}
              disabled={saving}
              rows={4}
              className="w-full px-3 py-2 border border-ink-300 rounded-md shadow-sm focus:ring-navy-500 focus:border-navy-500"
              placeholder="Anything worth noting about this submission (e.g. why you're filling on behalf)..."
            />
          </div>

          {error && (
            <div className="p-4 bg-danger-bg border border-danger-border rounded-lg text-danger-fg flex items-center gap-2">
              <AlertCircle className="w-5 h-5 flex-shrink-0" />
              {error}
            </div>
          )}

          {/* Actions */}
          <div className="flex items-center justify-end gap-4">
            <button
              onClick={() => handleSave('draft')}
              disabled={saving}
              className="flex items-center gap-2 px-4 py-2 text-ink-700 bg-white border border-ink-300 rounded-lg hover:bg-ink-50 disabled:opacity-50"
            >
              <Save className="w-4 h-4" />
              Save as Draft
            </button>
            <button
              onClick={() => handleSave('submitted')}
              disabled={saving}
              className="flex items-center gap-2 px-6 py-2 bg-navy-600 text-white rounded-lg hover:bg-navy-700 disabled:opacity-50"
            >
              <Send className="w-4 h-4" />
              {existingIntention ? 'Update Submission' : 'Submit on Behalf'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminFillIntention;
